/**
 * `<ThemePicker/>` — top-bar dropdown that swaps the active theme.
 *
 * Replaces the prior Sun/Moon two-state toggle with a five-option
 * picker. Each option renders a 3-swatch palette preview (bg / accent /
 * accent-fg) so the user previews the theme without committing.
 *
 * Voice — quiet + professional (per `feedback_brand_pillars.md`):
 * "Theme" / "Pick a theme" — never marketing copy.
 *
 * Persistence is via `setTheme()` which writes to localStorage and
 * dispatches subscribers; no backend `/api/account/me/preferences`
 * round-trip yet (parking-lot per the dispatch brief).
 *
 * The inner `<ThemeOptionList/>` is exported so tests can drive it
 * directly without depending on Radix Dropdown's pointer-event
 * simulation, which happy-dom doesn't fully model.
 */
import { Check, Palette } from "lucide-react";
import { useEffect, useState } from "react";

import { cn } from "../lib/cn";
import { THEMES, type Theme, getTheme, setTheme, subscribeTheme } from "../lib/theme";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Tooltip,
} from "./ui";

export function ThemePicker() {
  return (
    <DropdownMenu>
      <Tooltip content="Theme">
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Pick a theme"
            className="h-8 w-8 text-fg-subtle hover:text-fg"
          >
            <Palette className="h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent align="end" className="min-w-[16rem] p-2">
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <ThemeOptionList />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The five theme options as a radiogroup. Exported separately so the
 * Radix dropdown chrome can be skipped in tests (happy-dom doesn't
 * simulate the pointer events that Radix listens for).
 */
export function ThemeOptionList() {
  const [active, setActive] = useState<Theme>(() => getTheme());
  useEffect(() => subscribeTheme(setActive), []);

  return (
    <ul role="radiogroup" aria-label="Theme" className="flex flex-col gap-1">
      {THEMES.map((theme) => (
        <ThemeOption
          key={theme.id}
          theme={theme}
          active={theme.id === active}
          onPick={() => setTheme(theme.id)}
        />
      ))}
    </ul>
  );
}

interface ThemeOptionProps {
  theme: (typeof THEMES)[number];
  active: boolean;
  onPick: () => void;
}

function ThemeOption({ theme, active, onPick }: ThemeOptionProps) {
  return (
    <li>
      <button
        type="button"
        // biome-ignore lint/a11y/useSemanticElements: rich-content radio (swatch + label + description); <input type="radio"> can't compose this layout.
        role="radio"
        aria-checked={active}
        data-testid={`theme-option-${theme.id}`}
        onClick={onPick}
        className={cn(
          "flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          active
            ? "bg-accent-soft text-accent-fg"
            : "text-fg-muted hover:bg-bg-subtle hover:text-fg",
        )}
      >
        <SwatchPreview swatches={theme.swatches} />
        <span className="flex flex-1 flex-col">
          <span className="font-medium leading-tight">{theme.label}</span>
          <span className="text-xs text-fg-subtle leading-tight">{theme.description}</span>
        </span>
        {active ? <Check className="h-4 w-4 text-accent-fg" aria-hidden="true" /> : null}
      </button>
    </li>
  );
}

function SwatchPreview({
  swatches,
}: { swatches: { bg: string; accent: string; accentFg: string } }) {
  // Inline `style` is the only legitimate hex usage in the SPA — the
  // preview must render the theme's palette regardless of the active
  // theme, so we can't reach for a token here.
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-7 w-7 shrink-0 overflow-hidden rounded-md border border-border-strong"
    >
      <span className="h-full flex-1" style={{ backgroundColor: swatches.bg }} />
      <span className="h-full flex-1" style={{ backgroundColor: swatches.accent }} />
      <span className="h-full flex-1" style={{ backgroundColor: swatches.accentFg }} />
    </span>
  );
}
